// Demo view — coordination-story rebuild (v2).
//
// Six scrolling scenes, each doing one job:
//   1. Hook   — what is Planapus? (Story/Pitch voice, no jargon)
//   2. Read   — Planapus ingests the org's docs (live counter + snippet)
//   3. Map    — the shadow org graph; hero process pulsed
//   4. Port   — split-screen Today vs Planapus on one process
//   5. Run    — single hero vendor runs live + before/after reveal
//   6. Scale  — multi-process ROI table + on-ramp + CTA
//
// Eye-guidance primitives (numbered badges, curved-tail arrows) live
// in demo-eye.js. The persistent BackgroundGraph (force-directed,
// seeded) is layered behind every scene; Scene 3 promotes it to the
// foreground via data-act="3" CSS rules.

import { arrowFrom, stepBadge, ensureArrowMarker } from "./demo-eye";

// Multi-color brand marks for the integration tiles. Each entry is the
// authentic logo geometry from the brand's own press kit (Google Drive
// tri-color triangle, Slack four-color octothorpe, Microsoft Teams T
// in a purple tile, Atlassian Confluence interlocking arcs, the Notion
// outlined N, SharePoint angular pinwheel, Adobe PDF document badge,
// Oracle NetSuite N) — single-color silhouettes from Simple Icons
// don't read as the brand, so we draw the real marks directly. SVGs
// are sized for the 0..24 viewBox the caller already wraps them in.
//
// Notes on legibility:
//   • Marks sit edge-to-edge in the 24×24 viewBox (no inner disc) so
//     each brand reads as its own shape, not "letter on colored chip".
//   • The outer `.demo-read-source-bg` ring is cream-tinted, so a
//     transparent-background mark stays anchored to the row.
//   • White panels inside dark brand frames (Notion, Confluence) carry
//     the readable glyph; otherwise we'd lose the mark when the row
//     dims (`.is-disabled { opacity: 0.3 }`).
const BRAND_ICONS = {
  // Google Drive — the modern tri-color triangle. Three trapezoids:
  // yellow top, blue right wing, green bottom. Same geometry Google
  // ships in its press kit.
  gdrive: `
    <path fill="#FFCF63" d="M3.5 18.5 7.7 12 12 4.5 7.7 4.5z"/>
    <path fill="#0CB15A" d="M3.5 18.5 7.7 12l4.3 6.5z"/>
    <path fill="#4285F4" d="M20.5 18.5 16.3 12 12 4.5 16.3 4.5z"/>
    <path fill="#FFCF63" d="M16.3 4.5 12 4.5 7.7 4.5 12 11z"/>
    <path fill="#0F9D58" d="M3.5 18.5h17l-4.2-6.5H7.7z"/>
    <path fill="#EA4335" d="M12 4.5 7.7 12h8.6z"/>
  `,

  // SharePoint — Microsoft's angular two-tone "S" pinwheel inside a
  // teal rounded tile. SharePoint isn't in Simple Icons (Microsoft
  // brands are policed), so this is the press-kit silhouette.
  sharepoint: `
    <rect x="1" y="1" width="22" height="22" rx="3" fill="#036C70"/>
    <path fill="#fff" d="M11.4 6.2c2.5 0 4 1.1 4 2.9 0 1.3-.9 2.2-2.8 2.6l-1.8.4c-.7.2-1 .4-1 .8 0 .5.5.8 1.4.8 1.3 0 2.5-.5 3.5-1.2v2.7c-1 .5-2.3.8-3.7.8-2.5 0-4-1.1-4-2.9 0-1.3.9-2.2 2.8-2.6l1.8-.4c.7-.2 1-.4 1-.8 0-.5-.5-.8-1.4-.8-1.3 0-2.5.5-3.5 1.2V6.9c1-.5 2.3-.7 3.7-.7z"/>
  `,

  // Confluence — Atlassian's two interlocking blue curves. Built from
  // two arcs that overlap into a stylised "≈" shape, in the canonical
  // Atlassian Confluence blue gradient.
  confluence: `
    <rect x="1" y="1" width="22" height="22" rx="3" fill="#fff"/>
    <defs>
      <linearGradient id="cf-grad-a" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#0052CC"/>
        <stop offset="100%" stop-color="#2684FF"/>
      </linearGradient>
      <linearGradient id="cf-grad-b" x1="1" y1="1" x2="0" y2="0">
        <stop offset="0%" stop-color="#0052CC"/>
        <stop offset="100%" stop-color="#2684FF"/>
      </linearGradient>
    </defs>
    <path fill="url(#cf-grad-a)" d="M3.4 17.6c-.4.7-.2 1.5.5 1.9l3.4 1.9c.6.3 1.4.1 1.8-.5.1-.2 3.7-7.4 8.8-6.7.6.1 1.2-.3 1.3-.9l.8-3.5c.1-.6-.3-1.2-.9-1.3-9.3-1.5-13.8 4.8-15.7 9.1z"/>
    <path fill="url(#cf-grad-b)" d="M20.6 6.4c.4-.7.2-1.5-.5-1.9l-3.4-1.9c-.6-.3-1.4-.1-1.8.5-.1.2-3.7 7.4-8.8 6.7-.6-.1-1.2.3-1.3.9l-.8 3.5c-.1.6.3 1.2.9 1.3 9.3 1.5 13.8-4.8 15.7-9.1z"/>
  `,

  // Legacy PDFs — Adobe Acrobat red badge with white "PDF" text. Not
  // a vendor we integrate with; this is the file-format mark.
  pdfs: `
    <path fill="#B30B00" d="M5 2h11l3 3v17H5z"/>
    <path fill="#fff" opacity="0.25" d="M16 2v3h3z"/>
    <text x="12" y="17" text-anchor="middle" fill="#fff" font-family="Arial, sans-serif" font-size="6" font-weight="900">PDF</text>
  `,

  // Slack — official four-color octothorpe. Eight pill-shaped capsules
  // arranged so the bars overlap at the corners. The exact geometry
  // Slack ships on slack.com.
  slack: `
    <rect x="1" y="1" width="22" height="22" rx="3" fill="#fff"/>
    <rect x="6.6" y="13.4" width="4" height="9" rx="2" fill="#E01E5A"/>
    <rect x="6.6" y="13.4" width="9" height="4" rx="2" fill="#E01E5A"/>
    <rect x="1.6" y="6.6" width="9" height="4" rx="2" fill="#36C5F0"/>
    <rect x="6.6" y="1.6" width="4" height="9" rx="2" fill="#36C5F0"/>
    <rect x="13.4" y="1.6" width="4" height="9" rx="2" fill="#2EB67D"/>
    <rect x="13.4" y="6.6" width="9" height="4" rx="2" fill="#2EB67D"/>
    <rect x="13.4" y="13.4" width="9" height="4" rx="2" fill="#ECB22E"/>
    <rect x="13.4" y="13.4" width="4" height="9" rx="2" fill="#ECB22E"/>
  `,

  // Microsoft Teams — purple rounded square with white "T" centred.
  // The canonical Teams app-icon silhouette.
  teams: `
    <rect x="1" y="1" width="22" height="22" rx="3" fill="#5059C9"/>
    <rect x="3" y="3" width="18" height="18" rx="2" fill="#6264A7"/>
    <path fill="#fff" d="M16 8.5H8v2h3v6.5h2V10.5h3z"/>
  `,

  // Notion — outlined black "N" on white card. The official Notion
  // mark (white background, thick black border, inset N).
  notion: `
    <rect x="2" y="2" width="20" height="20" rx="2.5" fill="#fff" stroke="#000" stroke-width="1.5"/>
    <path fill="#000" d="M8 6.8 8 17.2H9.6V10l4.8 7.2h1.6V6.8h-1.6v7.2L9.6 6.8z"/>
  `,

  // Oracle NetSuite — red square with white "Ns" stacked-letter mark.
  // Oracle hasn't released NetSuite's mark as open SVG so this is the
  // recognizable wordmark approximation.
  netsuite: `
    <rect x="1" y="1" width="22" height="22" rx="3" fill="#C7281C"/>
    <text x="12" y="16.5" text-anchor="middle" fill="#fff" font-family="Arial, sans-serif" font-size="11" font-weight="800" letter-spacing="-0.5">Ns</text>
  `,
};

/**
 * Build the inner SVG markup for a brand icon — call from anywhere
 * inside demo.js. Returns a string of SVG children sized for a
 * 0..24 viewBox; the caller wraps it in an `<svg>` element.
 */
function brandIconSvg(id) {
  return BRAND_ICONS[id] || "";
}

const SCENES = [
  { id: 1, label: "Hook" },
  { id: 2, label: "Read" },
  { id: 3, label: "Map" },
  { id: 4, label: "Port" },
  { id: 5, label: "Run" },
  { id: 6, label: "Scale" },
];

const CLUSTERS = [
  { name: "Salesforce", dept: "GTM" },
  { name: "Workday",    dept: "HR" },
  { name: "Zendesk",    dept: "Support" },
  { name: "Jira",       dept: "Engineering" },
  { name: "Gainsight",  dept: "GTM" },
  { name: "Hubspot",    dept: "GTM" },
  { name: "DocuSign",   dept: "Legal" },
  { name: "Slack",      dept: "Operations" },
  { name: "NetSuite",   dept: "Finance" },
];

const DEPT_COLORS = {
  GTM:         "var(--brand-2)",
  Engineering: "var(--brand)",
  Support:     "var(--brand)",
  Finance:     "var(--bad)",
  Operations:  "var(--bad)",
  HR:          "var(--muted)",
  Legal:       "var(--muted)",
};

const GRAPH_W = 1200;
const GRAPH_H = 800;
// Match the original Act 1 safety bounds so the org-rectangle annotation
// can't escape the visible viewBox.
const SAFE = { L: 140, T: 140, R: 1080, B: 720 };

// ---------- Seeded PRNG ----------

let _seed = 0x9e3779b1;
function srand() {
  // Mulberry32 — deterministic, decent distribution, no globals leaked.
  _seed = (_seed + 0x6d2b79f5) | 0;
  let t = _seed;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
function srandReset() { _seed = 0x9e3779b1; }

// ---------- Graph generation ----------

function generateGraph() {
  srandReset();
  const nodes = [];
  const edges = [];
  let pid = 0;

  CLUSTERS.forEach((cluster, i) => {
    const sysId = `sys-${i}`;
    nodes.push({ id: sysId, type: "system", label: cluster.name, cluster: cluster.name, department: cluster.dept });
    const childCount = 5 + Math.floor(srand() * 5);
    for (let j = 0; j < childCount; j++) {
      const childId = `node-${pid++}`;
      nodes.push({ id: childId, type: "process", label: `Process ${childId}`, cluster: cluster.name, department: cluster.dept });
      edges.push({ id: `edge-${sysId}-${childId}`, source: sysId, target: childId, kind: "standard" });
    }
  });

  // 25 cross-cluster filaments (latent processes).
  for (let i = 0; i < 25; i++) {
    const sc = CLUSTERS[Math.floor(srand() * CLUSTERS.length)];
    let tc = CLUSTERS[Math.floor(srand() * CLUSTERS.length)];
    while (tc.name === sc.name) tc = CLUSTERS[Math.floor(srand() * CLUSTERS.length)];
    const srcs = nodes.filter(n => n.cluster === sc.name);
    const tgts = nodes.filter(n => n.cluster === tc.name);
    const s = srcs[Math.floor(srand() * srcs.length)];
    const t = tgts[Math.floor(srand() * tgts.length)];
    if (s && t) edges.push({ id: `cross-${i}`, source: s.id, target: t.id, kind: "standard" });
  }

  return { nodes, edges };
}

// ---------- Static force-directed layout ----------
// Reimplements d3-force's link + charge + center + collide for ~85 nodes
// over 300 static ticks. The original demo ticks once at load and never
// animates the layout — so we do the same: precompute, then never touch.

function layoutGraph(graph) {
  const { nodes, edges } = graph;
  const W = GRAPH_W, H = GRAPH_H;
  const byId = new Map(nodes.map(n => [n.id, n]));

  // Seed positions in a wide ring so the simulation starts in a usable state.
  nodes.forEach((n, i) => {
    const a = (i / nodes.length) * Math.PI * 2;
    n.x = W / 2 + Math.cos(a) * 200 + (srand() - 0.5) * 60;
    n.y = H / 2 + Math.sin(a) * 200 + (srand() - 0.5) * 60;
    n.vx = 0; n.vy = 0;
  });

  // Resolve edge endpoints to node refs once.
  const E = edges.map(e => ({
    s: byId.get(e.source),
    t: byId.get(e.target),
  })).filter(e => e.s && e.t);

  // Tuned for the closing scene's needs: enough spread that the four
  // corner-positioned labels each have a distinct subgraph segment near
  // them, not all bunched in the middle. Stronger repulsion, longer
  // springs, weaker center pull than d3 defaults.
  const LINK_DIST = 55;
  const CHARGE = -160;
  const COLLIDE_R = 24;
  const CENTER_K = 0.018;
  const ALPHA_DECAY = 0.0228;
  let alpha = 1;

  for (let tick = 0; tick < 400; tick++) {
    // Link force — spring each edge toward LINK_DIST.
    for (const { s, t } of E) {
      const dx = t.x - s.x;
      const dy = t.y - s.y;
      const d = Math.hypot(dx, dy) || 1;
      const k = ((d - LINK_DIST) / d) * 0.5 * alpha;
      s.x += dx * k; s.y += dy * k;
      t.x -= dx * k; t.y -= dy * k;
    }
    // Charge force — repulsion between every pair (O(n²), fine at n≈85).
    for (let a = 0; a < nodes.length; a++) {
      const na = nodes[a];
      for (let b = a + 1; b < nodes.length; b++) {
        const nb = nodes[b];
        const dx = nb.x - na.x;
        const dy = nb.y - na.y;
        let d2 = dx * dx + dy * dy;
        if (d2 < 1) d2 = 1;
        const f = (CHARGE / d2) * alpha;
        na.x += dx * f; na.y += dy * f;
        nb.x -= dx * f; nb.y -= dy * f;
      }
    }
    // Center force — gentle pull toward the middle.
    for (const n of nodes) {
      n.x += (W / 2 - n.x) * CENTER_K * alpha;
      n.y += (H / 2 - n.y) * CENTER_K * alpha;
    }
    // Collide — separate overlapping pairs at COLLIDE_R.
    for (let a = 0; a < nodes.length; a++) {
      const na = nodes[a];
      for (let b = a + 1; b < nodes.length; b++) {
        const nb = nodes[b];
        const dx = nb.x - na.x;
        const dy = nb.y - na.y;
        const d = Math.hypot(dx, dy) || 1;
        const overlap = COLLIDE_R * 2 - d;
        if (overlap > 0) {
          const push = (overlap / 2) * 0.7;
          const ux = dx / d, uy = dy / d;
          na.x -= ux * push; na.y -= uy * push;
          nb.x += ux * push; nb.y += uy * push;
        }
      }
    }
    alpha -= ALPHA_DECAY * alpha;
  }

  // Clamp into the safe rectangle so labels + annotations don't clip.
  for (const n of nodes) {
    n.x = Math.max(SAFE.L + 8, Math.min(SAFE.R - 8, n.x));
    n.y = Math.max(SAFE.T + 8, Math.min(SAFE.B - 8, n.y));
  }

  return { nodes, edges };
}

// Cached cross-edges + adjacency for the Act-1 hover highlight pipeline.
function buildHighlightIndex(graph) {
  const cross = graph.edges.filter(e => e.id.startsWith("cross-"));
  const adj = new Map();
  for (const e of graph.edges) {
    if (!adj.has(e.source)) adj.set(e.source, []);
    if (!adj.has(e.target)) adj.set(e.target, []);
    adj.get(e.source).push(e);
    adj.get(e.target).push(e);
  }
  return { cross, adj };
}

// ---------- DOM construction ----------

function svg(tag, attrs = {}, children = []) {
  const el = document.createElementNS("http://www.w3.org/2000/svg", tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  for (const c of children) el.appendChild(c);
  return el;
}
function el(tag, attrs = {}, children = []) {
  const e = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "class") e.className = v;
    else if (k === "html") e.innerHTML = v;
    else if (k === "text") e.textContent = v;
    else if (k.startsWith("data-")) e.setAttribute(k, String(v));
    else if (k.startsWith("on") && typeof v === "function") e.addEventListener(k.slice(2), v);
    else e.setAttribute(k, String(v));
  }
  for (const c of children) if (c) e.appendChild(c);
  return e;
}

// Reframe-of-act caption used in Act 3 and Act 4 to surface the
// end-to-end-automation framing customers asked for ("Planapus runs it,
// you watch"). Returns the element so callers decide where to insert it.

function renderBackgroundGraph(graph, hl) {
  // F17 Slice B: Tailwind utilities replace the bg-graph base styling
  // from pilot-engine.css. State classes (.is-highlighted /
  // .is-dimmed on edges + nodes) stay in CSS — the engine flips them
  // on descendants of the bg layer.
  const root = svg("svg", {
    class: "demo-bg-svg w-full h-full block",
    viewBox: `0 0 ${GRAPH_W} ${GRAPH_H}`,
    preserveAspectRatio: "xMidYMid slice",
  });

  // Org-bounding rectangle (Act 1 only — hidden by default, toggled by act class).
  const minX = graph.nodes.reduce((m, n) => Math.min(m, n.x), Infinity);
  const minY = graph.nodes.reduce((m, n) => Math.min(m, n.y), Infinity);
  const maxX = graph.nodes.reduce((m, n) => Math.max(m, n.x), -Infinity);
  const maxY = graph.nodes.reduce((m, n) => Math.max(m, n.y), -Infinity);
  const pad = 30;
  const orgX = Math.max(SAFE.L, minX - pad);
  const orgY = Math.max(SAFE.T, minY - pad);
  const orgX2 = Math.min(SAFE.R, maxX + pad);
  const orgY2 = Math.min(SAFE.B, maxY + pad);
  const orgGroup = svg("g", { class: "demo-bg-org" }, [
    svg("rect", {
      x: orgX, y: orgY, width: orgX2 - orgX, height: orgY2 - orgY,
      rx: 16, ry: 16,
      fill: "color-mix(in srgb, var(--brand) 2%, transparent)",
      stroke: "color-mix(in srgb, var(--brand) 45%, transparent)",
      "stroke-width": 1.25,
      "stroke-dasharray": "6 6",
    }),
  ]);
  // Label chip sits ENTIRELY ABOVE the org-rect's dashed top edge (with
   //  a small gap) so the dashed line isn't bisecting the chip. Previously
   //  the chip was centered on the top edge — the chip's solid fill should
   //  have hidden the line within its bounds but the visual still read as
   //  "line goes through the label." This sidesteps the question.
  const chipW = 172, chipH = 20;
  const cx = orgX + (orgX2 - orgX) / 2;
  const top = orgY;
  const chipGap = 6;
  const chipY = top - chipH - chipGap;
  orgGroup.appendChild(svg("rect", {
    x: cx - chipW / 2, y: chipY, width: chipW, height: chipH,
    rx: 3, ry: 3,
    fill: "var(--bg-1)",
    stroke: "color-mix(in srgb, var(--brand) 45%, transparent)",
    "stroke-width": 1,
  }));
  const orgLabel = svg("text", {
    x: cx, y: chipY + chipH / 2 + 3, "text-anchor": "middle",
    "font-family": "ui-monospace, SFMono-Regular, Menlo, monospace",
    "font-size": 9, "letter-spacing": 2,
    fill: "color-mix(in srgb, var(--brand) 80%, var(--text))",
  });
  orgLabel.textContent = "YOUR ORGANIZATION";
  orgGroup.appendChild(orgLabel);
  root.appendChild(orgGroup);

  // Edges
  const edgesG = svg("g", { class: "demo-bg-edges" });
  const byId = new Map(graph.nodes.map(n => [n.id, n]));
  for (const e of graph.edges) {
    const a = byId.get(e.source);
    const b = byId.get(e.target);
    if (!a || !b) continue;
    const line = svg("line", {
      class:
        "demo-bg-edge [stroke:oklch(98.6%_0.002_67.8/0.08)] [stroke-width:1] fill-none [transition:stroke_0.3s,stroke-opacity_0.3s]",
      "data-eid": e.id,
      x1: a.x, y1: a.y, x2: b.x, y2: b.y,
    });
    if (e.id.startsWith("cross-")) line.classList.add("demo-bg-edge-cross");
    edgesG.appendChild(line);
  }
  root.appendChild(edgesG);

  // Nodes
  const nodesG = svg("g", { class: "demo-bg-nodes" });
  for (const n of graph.nodes) {
    const color = DEPT_COLORS[n.department] || "var(--muted)";
    const c = svg("circle", {
      class: n.type === "system"
        ? "demo-bg-node demo-bg-node-system [fill:oklch(26.8%_0.011_36.5)] [stroke:oklch(98.6%_0.002_67.8/0.3)] [stroke-width:1.5] [transition:all_0.3s]"
        : "demo-bg-node [fill:oklch(21.4%_0.009_43.1)] [stroke:oklch(98.6%_0.002_67.8/0.2)] [stroke-width:1] [transition:all_0.3s]",
      "data-nid": n.id,
      cx: n.x, cy: n.y,
      r: n.type === "system" ? 6 : 3,
      style: `--node-color: ${color};`,
    });
    nodesG.appendChild(c);
  }
  root.appendChild(nodesG);

  return { root, edgesG, nodesG, orgGroup };
}

// Closing-scene label positions. Four corner anchors that match the
// original Closing.tsx layout (20/30, 70/20, 80/60, 30/70) but pushed
// closer to the actual corners so the central "Live v2" badge gets
// breathing room. Each pair: CSS percentages used by the HTML label
// overlay + the equivalent viewBox coordinates used to pick the nearest
// cross-edge as that label's subgraph segment.
function pickPrimaryClosingIdx(graph) {
  const cross = graph.edges.filter(e => e.id.startsWith("cross-"));
  const byId = new Map(graph.nodes.map(n => [n.id, n]));
  const cx = GRAPH_W / 2, cy = GRAPH_H / 2;
  let bestIdx = 0, bestD = Infinity;
  for (let i = 0; i < cross.length; i++) {
    const e = cross[i];
    const s = byId.get(e.source);
    const t = byId.get(e.target);
    if (!s || !t) continue;
    const midX = (s.x + t.x) / 2;
    const midY = (s.y + t.y) / 2;
    const d = Math.hypot(midX - cx, midY - cy);
    if (d < bestD) { bestD = d; bestIdx = i; }
  }
  return bestIdx;
}

// Pick the four cross-edges that will represent the "other latent processes"
// during the closing scene. For each LABEL_POSITION, pick the nearest unused
// cross-edge whose endpoints are NOT part of the primary subgraph — so each
// label sits next to a distinct segment of the mycelium.

// ---------- Scene 1 · Hook ----------
// Five-second "what is Planapus" in the Story/Pitch voice. One copy
// block, a 5-step mini-roadmap of the rest of the demo, one scroll cue.

function renderHook(onNext) {
  // F17 Slice B: Tailwind utilities replace the prior `.demo-hook-*`
  // rules in pilot-engine.css.
  const root = el("div", {
    class:
      "demo-hook relative flex flex-col items-center justify-center gap-4 text-center p-12 max-w-[900px] mx-auto",
  });

  root.appendChild(el("div", {
    class:
      "demo-hook-eyebrow font-mono text-xs font-semibold uppercase tracking-[0.18em] text-taupe-300",
    text: "AI-NATIVE COORDINATION FOR ENTERPRISE OPERATIONS",
  }));
  root.appendChild(el("h1", {
    class:
      "demo-hook-title m-0 font-display font-normal leading-[1.15] [letter-spacing:-0.015em] text-[clamp(2rem,4vw,3.25rem)] text-taupe-50 max-w-[880px]",
    text: "Planapus reads your org's documents, maps every coordinated process, then runs them better.",
  }));
  root.appendChild(el("p", {
    class:
      "demo-hook-sub m-0 text-[0.9375rem] text-taupe-300 max-w-[60ch]",
    style: "letter-spacing: 0.01em;",
    text: "A live walkthrough on a real procurement scenario — Halcyon Print & Mailing, vendor onboarding.",
  }));

  // 5-step mini-roadmap mirroring scenes 2–6.
  const STEPS = [
    { n: 1, label: "Read",  hint: "Ingest your docs" },
    { n: 2, label: "Map",   hint: "Reveal your processes" },
    { n: 3, label: "Port",  hint: "Translate to Planapus" },
    { n: 4, label: "Run",   hint: "Coordinated by AI" },
    { n: 5, label: "Win",   hint: "Before vs after" },
  ];
  const road = el("div", {
    class:
      "demo-hook-roadmap flex items-center flex-wrap justify-center gap-3 mt-3",
  });
  for (let i = 0; i < STEPS.length; i++) {
    const s = STEPS[i];
    const pill = el("div", {
      // The `--delay` custom prop is read by entrance animations that
      // are no longer wired up in this file but the data hook stays
      // so a future polish pass can re-introduce them without editing
      // this loop.
      class:
        "demo-hook-roadmap-pill inline-flex items-center gap-2.5 px-3.5 py-2 [border:1px_solid_oklch(98.6%_0.002_67.8/0.18)] [background:oklch(98.6%_0.002_67.8/0.04)] rounded-full text-xs text-taupe-200",
      style: `--delay: ${300 + i * 120}ms;`,
    }, [
      el("span", {
        class:
          "demo-hook-roadmap-num font-mono text-[11px] font-semibold text-taupe-300",
        text: String(s.n),
      }),
      el("div", { class: "demo-hook-roadmap-body" }, [
        el("div", { class: "demo-hook-roadmap-label", text: s.label }),
        el("div", { class: "demo-hook-roadmap-hint", text: s.hint }),
      ]),
    ]);
    road.appendChild(pill);
    if (i < STEPS.length - 1) {
      road.appendChild(el("span", {
        class: "demo-hook-roadmap-arrow text-taupe-300",
        style: `--delay: ${360 + i * 120}ms;`,
        text: "→",
      }));
    }
  }
  root.appendChild(road);

  root.appendChild(el("button", {
    class:
      "demo-hook-scroll inline-flex items-center gap-1.5 bg-transparent border-none text-taupe-300 cursor-pointer text-sm mt-4 hover:text-taupe-50 transition-colors",
    type: "button",
    onclick: () => onNext(),
  }, [
    el("span", { text: "Scroll" }),
    el("span", { class: "demo-hook-scroll-arrow", text: "↓" }),
  ]));

  return root;
}

// ---------- Scene 2 · Read ----------
// Planapus reads the org's actual artifacts. Five doc-source nodes on
// the left flow arrows into a central "brain" node. A counter ticks
// up (docs / messages / dependencies). A snippet card appears at the
// bottom showing what Planapus extracted from one specific document.

function renderRead(onNext) {
  // F17 (Slice A): Tailwind utilities replace the prior
  // `.demo-read-*` rules in pilot-engine.css. The leading legacy
  // class name (`demo-read`, `demo-read-head`, …) stays as a stable
  // hook for the per-step `.is-complete` / `.is-disabled` selectors
  // that ARE still in pilot-engine.css (those involve descendant
  // state which Tailwind utilities don't express cleanly inline).
  const root = el("div", {
    class:
      "demo-read relative grid grid-rows-[auto_1fr] gap-x-[clamp(1.5rem,4vw,3.5rem)] gap-y-4 mx-auto w-full max-w-[1280px] justify-center items-center px-[clamp(1.25rem,4vw,3rem)] pt-6 pb-36 text-taupe-50 z-10 " +
      "[grid-template-columns:clamp(420px,60vw,760px)_max-content] " +
      "[grid-template-areas:'head_head''canvas_counter']",
  });

  // Header (spans both grid columns).
  root.appendChild(el("div", {
    class:
      "demo-read-head flex flex-col gap-1 max-w-[720px] [grid-area:head]",
  }, [
    el("div", {
      class:
        "demo-read-eyebrow font-mono text-xs font-semibold uppercase tracking-[0.18em] text-taupe-400",
      text: "STEP 1 · READ",
    }),
    el("h2", {
      class:
        "demo-read-title font-display font-normal leading-tight text-[clamp(1.5rem,3vw,2.25rem)] text-taupe-50 m-0",
      text: "Planapus reads everything your team already writes.",
    }),
  ]));

  // Sources are grounded on real-world systems where procurement /
  // vendor-onboarding artifacts actually live. Numbers (docs, msgs,
  // deps) are realistic mid-market figures so the counter math
  // recomputes plausibly when the user toggles sources on/off. Default
  // 5 enabled = the typical 5 a mid-market SaaS or services co has;
  // the other 3 are alternatives (Teams instead of Slack), additions
  // (Notion), or ERP integrations (NetSuite) that some orgs run.
  //
  // Click any source row to toggle it. The arrow bundle + counter
  // targets recompute live; the "Run scan" button replays the
  // ingestion animation with the current set.
  const ALL_SOURCES = [
    { id: "gdrive",     name: "Google Drive · Contracts archive",       enabled: true,  docs: 8200, msgs: 0,      deps: 24 },
    { id: "sharepoint", name: "SharePoint · Master Service Agreements", enabled: true,  docs: 5400, msgs: 0,      deps: 18 },
    { id: "confluence", name: "Confluence · Vendor operations wiki",    enabled: true,  docs: 2400, msgs: 0,      deps: 16 },
    { id: "pdfs",       name: "Legacy PDFs · RFP & contract archive",   enabled: true,  docs: 2420, msgs: 0,      deps: 12 },
    { id: "slack",      name: "Slack · #procurement-intake",            enabled: true,  docs: 0,    msgs: 642000, deps: 26 },
    { id: "teams",      name: "Microsoft Teams · Sourcing channels",    enabled: false, docs: 0,    msgs: 280000, deps: 14 },
    { id: "notion",     name: "Notion · Procurement playbooks",         enabled: false, docs: 1580, msgs: 0,      deps: 8  },
    { id: "netsuite",   name: "NetSuite · Vendor master records",       enabled: false, docs: 470,  msgs: 0,      deps: 18 },
  ];

  // Canvas (SVG) for the hub-and-spoke + arrow draw-in animation.
  // ViewBox 0..1000 horizontal × 0..480 vertical. Content is shifted
  // right by ~100 vs the original so the visual (sources + arrows +
  // brain) sits centred horizontally on the page.
  const W = 1000, H = 480;
  // HUB.y is the vertical midpoint of the source rows (8 rows × 42 = 294,
  // starting at y=40, so midpoint = 40 + 294/2 = 187 — round to 188).
  const HUB = { x: 740, y: 188 };
  const SX = 210;
  // Spacing tightened to 42 (from 50) so all 8 rows fit above the
  // snippet card at bottom-left.
  const ROW_SPACING = 42;
  const sourceYs = ALL_SOURCES.map((_, i) => 40 + i * ROW_SPACING);

  // Integration icons resolve through `brandIconSvg` at module top so
  // the demo and the integrations catalog share one source for brand
  // colors and Simple Icons SVG paths. The "check" mark stays here
  // (it isn't a brand) — outline-style so the CSS color rule can
  // drive it green when the scrape completes.
  const CHECK_SVG = '<polyline points="20 6 9 17 4 12" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/>';
  function buildIcon(iconKey, x, y, size = 22, extraClass = "") {
    const wrap = svg("svg", {
      class: `demo-read-source-icon-svg${extraClass ? " " + extraClass : ""}`,
      x: x - size / 2,
      y: y - size / 2,
      width: size,
      height: size,
      viewBox: "0 0 24 24",
    });
    wrap.innerHTML = iconKey === "check" ? CHECK_SVG : brandIconSvg(iconKey);
    return wrap;
  }

  const canvas = svg("svg", {
    class:
      "demo-read-canvas block w-full h-auto [aspect-ratio:1000/480] [max-height:min(50vh,460px)] self-center [grid-area:canvas]",
    viewBox: `0 0 ${W} ${H}`,
    preserveAspectRatio: "xMidYMid meet",
  });
  ensureArrowMarker(canvas);

  // --- Brain halo first so arrows/pulses render above it ---
  const halo = svg("circle", {
    class: "demo-read-hub-halo [fill:oklch(98.6%_0.002_67.8/0.04)]",
    cx: HUB.x, cy: HUB.y, r: 56,
  });
  canvas.appendChild(halo);

  // Arrow geometry constants.
  // ARROW_FROM_X: clears the end of the longest source label.
  // END_X: just outside the core's left edge (core radius 36 + 8 gap).
  const ARROW_FROM_X = 530;
  const END_X = HUB.x - 44;

  // Arrows + pulses live in a group so we can clear and rebuild them
  // when the user toggles a source on/off. arrowsGroup is appended
  // BEFORE the core so arrowheads sit behind it (arrows arrive at,
  // not on top of, the brain).
  const arrowsGroup = svg("g", { class: "demo-read-arrows" });
  canvas.appendChild(arrowsGroup);

  function rebuildArrows() {
    while (arrowsGroup.firstChild) arrowsGroup.removeChild(arrowsGroup.firstChild);
    const enabled = ALL_SOURCES.filter(s => s.enabled);
    const N = enabled.length;
    if (N === 0) return;
    // End-y span grows with enabled count so the fan stays even.
    const endYSpan = Math.min(120, Math.max(16, (N - 1) * 18));
    const yStep = N > 1 ? endYSpan / (N - 1) : 0;
    for (let i = 0; i < N; i++) {
      const s = enabled[i];
      const sourceIdx = ALL_SOURCES.indexOf(s);
      const from = { x: ARROW_FROM_X, y: sourceYs[sourceIdx] };
      const to = {
        x: END_X,
        y: HUB.y + (i - (N - 1) / 2) * yStep,
      };
      const curve = (i - (N - 1) / 2) * 5;
      const arrow = arrowFrom(from, to, { curve, delay: 400 + i * 180 });
      const pathId = `demo-read-arrow-${s.id}`;
      arrow.setAttribute("id", pathId);
      arrowsGroup.appendChild(arrow);

      const beginSec = 1.2 + i * 0.28;
      const pulse = svg("circle", {
        class: "demo-read-pulse",
        r: 4,
        fill: "var(--brand-2)",
        cx: 0, cy: 0,
        style: `--pulse-delay: ${beginSec * 1000}ms;`,
      });
      const motion = svg("animateMotion", {
        dur: "2.2s",
        repeatCount: "indefinite",
        begin: `${beginSec}s`,
        rotate: "auto",
      });
      motion.appendChild(svg("mpath", { href: `#${pathId}` }));
      pulse.appendChild(motion);
      arrowsGroup.appendChild(pulse);
    }
  }
  rebuildArrows();

  // --- Brain core + label render AFTER arrows + pulses so they sit on
  //     top (the arrowheads arrive AT the core; pulses dissolve into it). ---
  const core = svg("circle", {
    class:
      "demo-read-hub-core [fill:oklch(98.6%_0.002_67.8)] [stroke:oklch(98.6%_0.002_67.8/0.6)] [stroke-width:1]",
    cx: HUB.x, cy: HUB.y, r: 36,
  });
  canvas.appendChild(core);
  const hubLabel = svg("text", {
    class:
      "demo-read-hub-label [fill:oklch(14.7%_0.004_49.3)] [font-family:Inter,sans-serif] [font-size:13px] font-semibold pointer-events-none",
    x: HUB.x, y: HUB.y + 4,
    "text-anchor": "middle",
  });
  hubLabel.textContent = "Planapus";
  canvas.appendChild(hubLabel);

  // Source nodes — icon-bg circle + branded icon + label. Click anywhere
  // on the row to toggle its enabled state. State classes:
  //   .is-disabled        — user has toggled it off (no arrow, dimmed)
  //   .is-scanning        — its arrow pulse is currently running
  //   parent .is-complete — scrape finished (enabled rows go green ✓)
  const sourceGs = [];
  for (let i = 0; i < ALL_SOURCES.length; i++) {
    const s = ALL_SOURCES[i];
    const y = sourceYs[i];
    // `demo-read-source` legacy hook stays because the per-row
     // `.is-disabled` + `.is-scanning` state classes drive descendant
     // styling (label strikethrough, bg ring colour) — Tailwind
     // doesn't express descendant-state cleanly inline, so the
     // remaining `.demo-read-source.is-*` rules live in pilot-engine.css.
    const g = svg("g", {
      class: `demo-read-source cursor-pointer${s.enabled ? "" : " is-disabled"}`,
      style: `--delay: ${200 + i * 100}ms;`,
      "data-source": s.id,
    });
    // Invisible wide click target so the whole row, not just the icon,
    // toggles. pointer-events:all on transparent fill keeps it
    // clickable.
    const hit = svg("rect", {
      class: "demo-read-source-hit cursor-pointer",
      x: SX - 18, y: y - 16,
      width: 340, height: 32,
      fill: "transparent",
    });
    g.appendChild(hit);
    g.appendChild(svg("circle", {
      class:
        "demo-read-source-bg [fill:oklch(26.8%_0.011_36.5)] [stroke:oklch(98.6%_0.002_67.8/0.25)] [stroke-width:1]",
      cx: SX, cy: y, r: 14,
    }));
    g.appendChild(buildIcon(s.id, SX, y, 22));
    g.appendChild(buildIcon("check", SX, y, 16, "demo-read-source-check pointer-events-none opacity-0 [fill:oklch(75%_0.15_145)] [stroke:oklch(75%_0.15_145)] transition-opacity duration-300"));
    const label = svg("text", {
      class:
        "demo-read-source-label [fill:oklch(96%_0.002_17.2)] [font-family:Inter,sans-serif] [font-size:13px] [dominant-baseline:middle] pointer-events-none",
      x: SX + 24, y: y + 4,
    });
    label.textContent = s.name;
    g.appendChild(label);
    // Toggle handler — wired on the hit rect so the entire row is
    // clickable. addEventListener (not onclick attr) so the SVG
    // namespace doesn't choke on it.
    hit.addEventListener("click", () => toggleSource(i));
    canvas.appendChild(g);
    sourceGs.push(g);
  }

  root.appendChild(canvas);

  // Counter strip — sits in the grid's right column, vertically
  // centered with the canvas so "DOCUMENTS READ" aligns with the
  // source list rows instead of pinning to the scene top.
  const counter = el("div", {
    class:
      "demo-read-counter flex flex-col gap-5 items-end text-right z-[5] pointer-events-none self-center justify-self-end [grid-area:counter]",
  });
  const mkCount = (label, key, accent) => {
    const wrap = el("div", {
      class: "demo-read-count flex flex-col gap-0.5",
    }, [
      el("div", {
        class:
          "demo-read-count-label font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-taupe-400",
        text: label,
      }),
      el("div", {
        class:
          `demo-read-count-val font-display text-[clamp(1.5rem,2.25vw,2rem)] [font-variant-numeric:tabular-nums] leading-tight ${accent ? "is-accent [color:oklch(82%_0.15_35)]" : "text-taupe-50"}`,
        text: "0",
      }),
    ]);
    wrap.dataset.key = key;
    return wrap;
  };
  counter.appendChild(mkCount("Documents read",   "docs",  false));
  counter.appendChild(mkCount("Messages indexed", "msgs",  false));
  counter.appendChild(mkCount("Dependencies",     "deps",  true));
  root.appendChild(counter);

  // Snippet card — bottom-center, narrow. `demo-read-snippet`
  // legacy hook stays because the entrance animation depends on the
  // root's `.is-complete` state, which the engine toggles when the
  // counter finishes. The fade-in CSS for that selector still lives
  // in pilot-engine.css.
  const snippet = el("div", {
    class:
      "demo-read-snippet absolute bottom-20 left-1/2 max-w-[540px] p-4 px-5 [border:1px_solid_oklch(98.6%_0.002_67.8/0.18)] [background:oklch(14.7%_0.004_49.3/0.65)] rounded-xl flex flex-col gap-2 opacity-0 [transform:translate(-50%,8px)] [transition:opacity_0.6s_ease,transform_0.6s_ease] z-[5] [backdrop-filter:blur(4px)]",
  }, [
    el("div", {
      class:
        "demo-read-snippet-eyebrow font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-taupe-400",
      text: "What Planapus learned from §3.2 of VendorOnboarding_v2024.pdf",
    }),
    el("div", {
      class:
        "demo-read-snippet-quote text-[0.9375rem] italic text-taupe-50",
      text: "\"CSM drafts exec summary within 4h SLA. Routes to Deal Desk for $> $50K.\"",
    }),
    el("div", {
      class: "demo-read-snippet-extract text-[0.8125rem] text-taupe-300",
      text: "→ Step 1 of the operation: Draft vendor brief · Agent SLA 0h · escalates >$50K",
    }),
  ]);
  root.appendChild(snippet);

  // Run-scan button — always visible. Replays the ingestion animation
  // with the currently-enabled source set. Bottom-left of the scene.
  const runBtn = el("button", {
    class:
      "demo-read-run absolute bottom-6 left-[clamp(1.25rem,4vw,3rem)] inline-flex items-center gap-2 px-5 py-2 rounded-full [border:1px_solid_oklch(98.6%_0.002_67.8/0.3)] bg-transparent text-taupe-50 text-sm cursor-pointer hover:bg-[oklch(98.6%_0.002_67.8/0.08)] hover:[border-color:oklch(98.6%_0.002_67.8/0.5)] transition-colors z-[6]",
    type: "button",
  }, [
    el("span", { class: "demo-read-run-glyph text-base leading-none", text: "↻" }),
    el("span", { text: "Run scan" }),
  ]);
  root.appendChild(runBtn);

  // Skip / next button — hidden until counter completes. Bottom-right.
  // `demo-read-next` legacy hook stays because the fade-in is gated
  // by the root's `.is-complete` state — kept as CSS rule.
  const next = el("button", {
    class:
      "demo-read-next absolute bottom-6 right-[clamp(1.25rem,4vw,3rem)] inline-flex items-center gap-2 px-5 py-2 rounded-full border-none bg-taupe-50 text-taupe-950 text-sm font-medium cursor-pointer opacity-0 pointer-events-none transition-opacity duration-300 hover:bg-taupe-200 z-[6]",
    type: "button",
    onclick: () => onNext(),
  }, [
    el("span", { text: "Continue to the map" }),
    el("span", { class: "demo-read-next-arrow inline-flex items-center", text: "→" }),
  ]);
  root.appendChild(next);

  // --- Counter math ---
  // Targets recompute from whichever sources are currently enabled.
  // Hand-tuned per-source contributions in ALL_SOURCES; here we sum.
  function currentTargets() {
    let docs = 0, msgs = 0, deps = 0;
    for (const s of ALL_SOURCES) {
      if (!s.enabled) continue;
      docs += s.docs;
      msgs += s.msgs;
      deps += s.deps;
    }
    return { docs, msgs, deps: Math.max(0, deps) };
  }
  function fmtMsgs(n) {
    // Display as Xk when under 1M, X.XM when over.
    if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
    return `${Math.floor(n / 1000).toLocaleString()}K`;
  }
  const DUR_MS = 2880;    // 20% longer than the original 2400 — gives
                          // the counter more time to read as "scrape in
                          // progress" before snapping to the target.
  const DELAY_MS = 1200;  // wait for arrows to draw before counter starts
  let raf = null;
  let startTimer = null;
  const scanTimers = [];
  const setCount = (key, text) => {
    const node = counter.querySelector(`[data-key="${key}"] .demo-read-count-val`);
    if (node) node.textContent = text;
  };
  function setCountsSnap(targets) {
    setCount("docs", targets.docs.toLocaleString());
    setCount("msgs", fmtMsgs(targets.msgs));
    setCount("deps", targets.deps.toLocaleString());
  }
  function animateCounter(from = { docs: 0, msgs: 0, deps: 0 }) {
    const targets = currentTargets();
    const t0 = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / DUR_MS);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      const docs = Math.floor(from.docs + eased * (targets.docs - from.docs));
      const msgs = Math.floor(from.msgs + eased * (targets.msgs - from.msgs));
      const deps = Math.floor(from.deps + eased * (targets.deps - from.deps));
      setCount("docs", docs.toLocaleString());
      setCount("msgs", fmtMsgs(msgs));
      setCount("deps", deps.toLocaleString());
      if (t < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        root.classList.add("is-complete");
      }
    };
    raf = requestAnimationFrame(tick);
  }
  function startScanTimers() {
    // Each ENABLED source flips to .is-scanning at the same moment its
    // arrow pulse begins. Disabled sources stay struck-through.
    const enabled = ALL_SOURCES.filter(s => s.enabled);
    for (let i = 0; i < enabled.length; i++) {
      const sourceIdx = ALL_SOURCES.indexOf(enabled[i]);
      const t = setTimeout(() => sourceGs[sourceIdx].classList.add("is-scanning"),
        1200 + i * 280);
      scanTimers.push(t);
    }
  }
  function clearAllScanState() {
    for (const t of scanTimers) clearTimeout(t);
    scanTimers.length = 0;
    for (const g of sourceGs) g.classList.remove("is-scanning");
  }
  function applySourceDisabled() {
    for (let i = 0; i < ALL_SOURCES.length; i++) {
      sourceGs[i].classList.toggle("is-disabled", !ALL_SOURCES[i].enabled);
    }
  }

  // Toggle a source. The arrow bundle rebuilds for the new set AND the
  // full ingestion animation restarts — so the user sees the scrape
  // replay every time they reconfigure, not just on the explicit
  // "Run scan" button.
  function toggleSource(i) {
    ALL_SOURCES[i].enabled = !ALL_SOURCES[i].enabled;
    applySourceDisabled();
    runScan();
  }

  // Run scan — resets and replays the ingestion animation against
  // whatever set of sources is currently enabled.
  function runScan() {
    if (raf) cancelAnimationFrame(raf);
    if (startTimer) clearTimeout(startTimer);
    clearAllScanState();
    root.classList.remove("is-complete");
    setCountsSnap({ docs: 0, msgs: 0, deps: 0 });
    rebuildArrows();
    startTimer = setTimeout(() => animateCounter(), DELAY_MS);
    startScanTimers();
  }

  // Wire the run-scan button (added in CSS section below).
  runBtn.addEventListener("click", runScan);

  // Initial state.
  startTimer = setTimeout(() => animateCounter(), DELAY_MS);
  startScanTimers();

  root.__cleanup = () => {
    if (raf) cancelAnimationFrame(raf);
    if (startTimer) clearTimeout(startTimer);
    clearAllScanState();
  };
  root.__reset = () => {
    if (raf) cancelAnimationFrame(raf);
    if (startTimer) clearTimeout(startTimer);
    clearAllScanState();
    root.classList.remove("is-complete");
    setCountsSnap({ docs: 0, msgs: 0, deps: 0 });
    rebuildArrows();
    startTimer = setTimeout(() => animateCounter(), DELAY_MS);
    startScanTimers();
  };
  return root;
}

// ---------- Scene 3 · Map ----------
// The shadow org graph — the background SVG that already renders behind
// every scene gets promoted to the foreground. Department-coloured
// nodes, dotted org rectangle, one bright highlighted cross-edge for
// the hero process. HTML overlays add a top-centre callout, a numbered
// arrow tagged with the hero process name + impact, and the bottom-left
// 4-department legend.

function renderMap(onNext) {
  const root = el("div", { class: "demo-map" });

  // Top-centre headline callout.
  root.appendChild(el("div", { class: "demo-map-headline" }, [
    el("div", { class: "demo-map-eyebrow", text: "STEP 2 · MAP" }),
    el("h2", { class: "demo-map-title", text:
      "Your org's coordinated processes — derived from the docs." }),
    el("div", { class: "demo-map-subhead", text:
      "87 coordinated processes discovered · 23 are high-value." }),
  ]));

  // Hero callout — the numbered arrow + label that names the one
  // process we're going to walk through next. Positioned at top-right;
  // a thin SVG arrow draws from the badge toward the graph centre
  // (where applyBgHighlight lights up the most-central cross-edge —
  // see primaryClosingIdx).
  const hero = el("div", { class: "demo-map-hero" }, [
    stepBadge(1, "The long-tail vendor approval bottleneck", { align: "right", delay: 600 }),
    el("div", { class: "demo-map-hero-impact", text: "$4.6M leakage / yr · 60 vendors / qtr" }),
  ]);
  root.appendChild(hero);

  // Secondary callout — gestures at the rest of the map.
  root.appendChild(el("div", { class: "demo-map-secondary" }, [
    stepBadge(2, "22 more like this — we'll come back to them", { align: "right", delay: 900 }),
  ]));

  // Bottom-left legend (4 departments, matches the graph colouring).
  const legend = el("div", { class: "demo-map-legend" });
  legend.appendChild(el("div", { class: "demo-map-legend-head", text: "Org Topology" }));
  const LEGEND_ITEMS = [
    { color: "var(--brand-2)", text: "Procurement & Sourcing" },
    { color: "var(--brand)",   text: "IT & Security" },
    { color: "var(--bad)",     text: "Finance & AP" },
    { color: "var(--muted)",   text: "Legal & Compliance" },
  ];
  for (const it of LEGEND_ITEMS) {
    legend.appendChild(el("div", { class: "demo-map-legend-item" }, [
      el("span", { class: "demo-map-legend-dot", style: `--c: ${it.color};` }),
      el("span", { class: "demo-map-legend-text", text: it.text }),
    ]));
  }
  root.appendChild(legend);

  // Continue CTA.
  root.appendChild(el("button", {
    class: "demo-map-next",
    type: "button",
    onclick: () => onNext(),
  }, [
    el("span", { text: "Port a process →" }),
  ]));

  // Hover tooltip + cross-process highlighting. When a visitor hovers
  // any node that belongs to a latent process (a cross-cluster edge
  // and its adjacency neighbourhood), that subgraph gets the bright
  // highlight treatment AND the tooltip names the process. On
  // mouseleave the highlight reverts to the hero (long-tail vendor).
  const tooltip = el("div", { class: "demo-map-tooltip", hidden: true }, [
    el("div", { class: "demo-map-tooltip-name" }),
    el("div", { class: "demo-map-tooltip-impact" }),
  ]);
  root.appendChild(tooltip);
  const tipNameEl = tooltip.querySelector(".demo-map-tooltip-name");
  const tipImpactEl = tooltip.querySelector(".demo-map-tooltip-impact");

  // Map graph department keys → human-friendly labels matching the legend.
  const DEPT_LABEL = {
    GTM:         "Procurement & Sourcing",
    Engineering: "IT & Security",
    Support:     "IT & Security",
    Finance:     "Finance & AP",
    Operations:  "Finance & AP",
    HR:          "Legal & Compliance",
    Legal:       "Legal & Compliance",
  };
  // Names for the four non-hero processes the user is most likely to
  // hover into — pulled from the Closing scene's SECONDARY_PROCESS_NAMES
  // so the demo's process vocabulary stays coherent. Other cross-edges
  // fall back to a synthesized "Latent process · <dept-a> ↔ <dept-b>"
  // label so every hover lands on something nameable.
  const NAMED_PROCESSES = [
    { name: "Renewal price-creep on enterprise contracts", impact: "$1.2M overspend exposure" },
    { name: "Security & DPA review backlog",               impact: "38% of vendors waiting > 30 days" },
    { name: "Insurance & W-9 expiry tracking",             impact: "14% of vendors blocked" },
    { name: "Sole-source justification gaps",              impact: "$620K policy-violating spend" },
    { name: "Diversity-spend reporting reconciliation",    impact: "22 hrs / qtr manual rollup" },
    { name: "Contract clause drift (MSA ↔ SOW)",           impact: "9% of SOWs override MSA terms" },
  ];

  // Build a node→process map. For each cross-edge we mark its source
  // and target nodes plus a small adjacency neighbourhood (1 hop) so
  // hovering ANY node in that subgraph triggers its process highlight.
  // If a node is reachable from multiple cross-edges the closer one
  // wins (we map endpoints before adjacency).
  const nodeToProcessIdx = new Map();
  // Stable ordering for the named-process pool: hero first, then the
  // remaining cross-edges in their natural index order.
  const orderedCrossIdx = [];
  if (highlightIndex && highlightIndex.cross) {
    orderedCrossIdx.push(primaryClosingIdx);
    for (let i = 0; i < highlightIndex.cross.length; i++) {
      if (i !== primaryClosingIdx) orderedCrossIdx.push(i);
    }
    // Map endpoints first (higher priority).
    for (const i of orderedCrossIdx) {
      const e = highlightIndex.cross[i];
      if (!e) continue;
      if (!nodeToProcessIdx.has(e.source)) nodeToProcessIdx.set(e.source, i);
      if (!nodeToProcessIdx.has(e.target)) nodeToProcessIdx.set(e.target, i);
    }
    // Then 1-hop neighbours (lower priority).
    for (const i of orderedCrossIdx) {
      const e = highlightIndex.cross[i];
      if (!e) continue;
      for (const endpoint of [e.source, e.target]) {
        const adj = highlightIndex.adj.get(endpoint) || [];
        for (const adjE of adj.slice(0, 4)) {
          const other = adjE.source === endpoint ? adjE.target : adjE.source;
          if (!nodeToProcessIdx.has(other)) nodeToProcessIdx.set(other, i);
        }
      }
    }
  }

  function processInfo(processIdx) {
    if (processIdx === primaryClosingIdx) {
      return {
        name: "Long-tail vendor approval bottleneck",
        impact: "$4.6M leakage / yr · 60 vendors / qtr",
      };
    }
    // Non-hero: pull a named entry by stable rank order, or synthesize
    // from the cross-edge's two endpoint departments.
    const rank = orderedCrossIdx.indexOf(processIdx); // 0 = hero, 1.. = others
    if (rank > 0 && rank <= NAMED_PROCESSES.length) {
      return NAMED_PROCESSES[rank - 1];
    }
    const e = highlightIndex.cross[processIdx];
    if (!e) return null;
    const byId = new Map(cachedGraph.nodes.map(n => [n.id, n]));
    const s = byId.get(e.source);
    const t = byId.get(e.target);
    const sDept = DEPT_LABEL[s?.department] || "?";
    const tDept = DEPT_LABEL[t?.department] || "?";
    const baseK = 80 + (processIdx * 47) % 380; // 80K–460K, stable per index
    return {
      name: sDept === tDept ? `Latent process · ${sDept}` : `Latent process · ${sDept} ↔ ${tDept}`,
      impact: `~$${baseK}K leakage / yr`,
    };
  }

  // Track the currently-highlighted process so we don't churn
  // applyBgHighlight on every mousemove (only re-fire when it changes).
  let currentHighlightIdx = primaryClosingIdx;
  function setHighlight(idx) {
    if (idx === currentHighlightIdx) return;
    currentHighlightIdx = idx;
    if (typeof applyBgHighlight === "function") applyBgHighlight(idx);
  }
  function revertHighlight() { setHighlight(primaryClosingIdx); }

  function positionTooltip(mx, my) {
    const section = document.getElementById("demo");
    const sectRect = section.getBoundingClientRect();
    const tipW = 280, tipH = 56;
    let left = mx - sectRect.left + 14;
    let top = my - sectRect.top - tipH - 8;
    if (left + tipW > sectRect.width - 8) left = mx - sectRect.left - tipW - 14;
    if (top < 8) top = my - sectRect.top + 18;
    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }

  function showTooltip(nodeData, mx, my) {
    const procIdx = nodeToProcessIdx.get(nodeData.id);
    if (procIdx !== undefined) {
      setHighlight(procIdx);
      const info = processInfo(procIdx);
      if (info) {
        tipNameEl.textContent = info.name;
        tipImpactEl.textContent = info.impact;
      }
    } else {
      // Node not in any process subgraph — keep the current highlight
      // and show a generic department-flavoured card.
      const deptLabel = DEPT_LABEL[nodeData.department] || nodeData.department;
      const isSystem = nodeData.type === "system";
      tipNameEl.textContent = isSystem
        ? `${nodeData.label} · ${deptLabel}`
        : `Process · ${deptLabel}`;
      tipImpactEl.textContent = isSystem ? "Connected to several processes" : "Not on a high-value cross-edge";
    }
    positionTooltip(mx, my);
    tooltip.hidden = false;
  }
  function hideTooltip() {
    tooltip.hidden = true;
    revertHighlight();
  }

  // Hover handlers go on the .demo-map root itself, not on each bg-graph
  // node. The bg-graph layer is z-stacked BELOW .demo-scroller (z:10) so
  // pointer events never reach the SVG circles directly. Instead .demo-map
  // captures mousemove and we hit-test against cached node positions
  // using the SVG's inverse CTM. Works regardless of layer order.
  let mapMoveHandler = null;
  let mapLeaveHandler = null;
  function hitTestNode(clientX, clientY) {
    if (!bg || !cachedGraph) return null;
    const svgEl = bg.root;
    const ctm = svgEl.getScreenCTM();
    if (!ctm) return null;
    const pt = svgEl.createSVGPoint();
    pt.x = clientX;
    pt.y = clientY;
    const vb = pt.matrixTransform(ctm.inverse());
    // 14 viewBox units ≈ 18px on screen at typical viewports — generous
    // enough that the user doesn't have to hit a 3px process node dead-on.
    const HIT_R_VB = 14;
    let best = null;
    let bestD2 = HIT_R_VB * HIT_R_VB;
    for (const n of cachedGraph.nodes) {
      const dx = n.x - vb.x;
      const dy = n.y - vb.y;
      const d2 = dx * dx + dy * dy;
      if (d2 < bestD2) { bestD2 = d2; best = n; }
    }
    return best;
  }
  function wireGraphHover() {
    if (mapMoveHandler) return;
    mapMoveHandler = (e) => {
      const node = hitTestNode(e.clientX, e.clientY);
      if (node) showTooltip(node, e.clientX, e.clientY);
      else hideTooltip();
    };
    mapLeaveHandler = () => hideTooltip();
    root.addEventListener("mousemove", mapMoveHandler);
    root.addEventListener("mouseleave", mapLeaveHandler);
  }
  function unwireGraphHover() {
    if (mapMoveHandler) {
      root.removeEventListener("mousemove", mapMoveHandler);
      root.removeEventListener("mouseleave", mapLeaveHandler);
      mapMoveHandler = null;
      mapLeaveHandler = null;
    }
    tooltip.hidden = true;
  }

  // __reset is called by setActiveScene on re-entry. Reset to the hero
  // process and re-wire hover handlers (the bg-graph layer is shared
  // across scenes, so handlers attached on enter must be torn down on
  // leave to avoid leaking).
  root.__reset = () => {
    currentHighlightIdx = -1; // force setHighlight to actually fire
    setHighlight(primaryClosingIdx);
    unwireGraphHover();
    wireGraphHover();
  };
  root.__cleanup = () => {
    if (typeof applyBgHighlight === "function") applyBgHighlight(null);
    currentHighlightIdx = -1;
    unwireGraphHover();
  };
  // Fire once on first mount (setActiveScene calls __reset on re-entry
  // but not on the first transition into the scene).
  setTimeout(() => root.__reset(), 0);

  return root;
}

// ---------- Scene 4 · Port & configure ----------
// Side-by-side: today's manual coordination vs Planapus's coordinated
// coordinated operation for the same 5-step vendor onboarding. Left column reads
// rough/hand-drawn; right column reads clean. Two numbered arrows
// span the gutter to teach the eye ① "We port each step" → ② "Planapus
// picks the right hand-off."

function renderPort(onNext) {
  // F17 (Slice A): Tailwind utilities replace the prior `.demo-port-*`
  // rules in pilot-engine.css. Most styling is now inline; only the
  // `.is-active` toggle highlight and the `.demo-port-col-planapus`
  // accent color overrides for nested children that need descendant
  // selectors stay in CSS.
  const root = el("div", {
    class:
      "demo-port relative flex flex-col gap-6 w-full max-w-[1280px] mx-auto px-[clamp(1.25rem,4vw,3rem)] pt-6 pb-20 text-taupe-50 z-10",
  });

  // Header
  root.appendChild(el("div", {
    class: "demo-port-head flex flex-col gap-1 max-w-[760px]",
  }, [
    el("div", {
      class:
        "demo-port-eyebrow font-mono text-xs font-semibold uppercase tracking-[0.18em] text-taupe-400",
      text: "STEP 3 · PORT",
    }),
    el("h2", {
      class:
        "demo-port-title font-display font-normal leading-tight text-[clamp(1.5rem,3vw,2.25rem)] text-taupe-50 m-0",
      text: "We port your process from manual coordination to Planapus.",
    }),
  ]));

  // Shared step shape: name + delegate + annotation hint.
  const TODAY_STEPS = [
    { name: "Intake",                delegate: "Alice (Procurement Lead)",  hint: "+4h wait" },
    { name: "Security check",        delegate: "Charlie (Security)",        hint: "Out 2d" },
    { name: "Finance approval",      delegate: "Bob (CFO Approver)",        hint: "2× rework" },
    { name: "Insurance / W-9",       delegate: "Diana (AP Manager)",        hint: "missing doc" },
    { name: "Vendor activation",     delegate: "Eve (Onboarding Spec.)",    hint: "manual entry" },
  ];
  // Lucide-equivalent inline SVG icons for the four delegation types.
  const ICONS = {
    bot:       '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>',
    user:      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
    briefcase: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg>',
    slack:     '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>',
  };

  // Each Planapus step has 4 delegation options the user can toggle
  // through. Default `pick` is the option Planapus recommends. Stats
  // (days/labor/onPolicy) recompute as the user changes picks.
  const PLANAPUS_STEPS = [
    {
      name: "Draft vendor brief", pick: "Agent",
      options: {
        Agent:      { delegate: "VendorBriefBot",            sla: "INSTANT", days: 0,   labor: 0,   onPolicyDelta: 5 },
        Individual: { delegate: "Alice (Procurement Lead)",  sla: "4h SLA",  days: 0.2, labor: 2,   onPolicyDelta: 0 },
        Role:       { delegate: "Category Manager",          sla: "4h SLA",  days: 0.2, labor: 2,   onPolicyDelta: 0 },
        Group:      { delegate: "#procurement-team",         sla: "12h SLA", days: 0.5, labor: 4,   onPolicyDelta: -2 },
      },
    },
    {
      name: "Security & DPA review", pick: "Individual",
      options: {
        Agent:      { delegate: "VantaTriage",                  sla: "INSTANT", days: 0,   labor: 0,   onPolicyDelta: 5 },
        Individual: { delegate: "Charlie (Security Reviewer)",  sla: "8h SLA",  days: 1.0, labor: 2,   onPolicyDelta: 0 },
        Role:       { delegate: "Security Reviewer (of record)", sla: "8h SLA", days: 1.0, labor: 2,  onPolicyDelta: 0 },
        Group:      { delegate: "#security-review",             sla: "24h SLA", days: 2.0, labor: 3,  onPolicyDelta: -2 },
      },
    },
    {
      name: "Finance approval", pick: "Agent",
      options: {
        Agent:      { delegate: "BudgetMatcher",         sla: "INSTANT", days: 0,   labor: 0,   onPolicyDelta: 5 },
        Individual: { delegate: "Bob (CFO Approver)",    sla: "8h SLA",  days: 1.0, labor: 1,   onPolicyDelta: 0 },
        Role:       { delegate: "Category Budget Owner", sla: "8h SLA",  days: 1.0, labor: 1,   onPolicyDelta: 0 },
        Group:      { delegate: "#finance-approvers",    sla: "24h SLA", days: 2.0, labor: 3,   onPolicyDelta: -2 },
      },
    },
    {
      name: "Insurance & W-9", pick: "Group",
      options: {
        Agent:      { delegate: "DocCollector",        sla: "INSTANT", days: 0,   labor: 0,    onPolicyDelta: 5 },
        Individual: { delegate: "Diana (AP Manager)",  sla: "5h SLA",  days: 0.5, labor: 2,    onPolicyDelta: 0 },
        Role:       { delegate: "AP Manager",          sla: "6h SLA",  days: 0.5, labor: 2,    onPolicyDelta: 0 },
        Group:      { delegate: "#vendor-docs",        sla: "18h SLA", days: 1.5, labor: 0.5,  onPolicyDelta: -1 },
      },
    },
    {
      name: "Activate & enroll", pick: "Agent",
      options: {
        Agent:      { delegate: "VendorMasterBot",                       sla: "INSTANT", days: 0,   labor: 0,   onPolicyDelta: 5 },
        Individual: { delegate: "Eve (Vendor Onboarding Specialist)",    sla: "3h SLA",  days: 0.2, labor: 1.5, onPolicyDelta: 0 },
        Role:       { delegate: "Vendor Onboarding Specialist",          sla: "3h SLA",  days: 0.2, labor: 1.5, onPolicyDelta: 0 },
        Group:      { delegate: "#vendor-master",                        sla: "24h SLA", days: 1.0, labor: 4,   onPolicyDelta: -2 },
      },
    },
  ];

  // Delegation-type → icon + color token. Used for the toggle buttons
  // AND the selected-delegate avatar.
  const TYPE_META = {
    Agent:      { icon: ICONS.bot,       label: "Agent",      color: "var(--brand-2)" },
    Individual: { icon: ICONS.user,      label: "Individual", color: "var(--brand)"   },
    Role:       { icon: ICONS.briefcase, label: "Role",       color: "var(--brand-2)" },
    Group:      { icon: ICONS.slack,     label: "Group",      color: "var(--brand)"   },
  };
  const TYPE_ORDER = ["Agent", "Individual", "Role", "Group"];

  // Two columns + a thin gutter for the eye-guide badges.
  const cols = el("div", {
    class:
      "demo-port-cols grid grid-cols-[1fr_auto_1fr] gap-[clamp(1.5rem,3vw,2.5rem)] items-stretch max-lg:grid-cols-1",
  });

  // Shared column shell — bordered card with accent left edge.
  const COL_BASE =
    "demo-port-col flex flex-col gap-3 pt-5 px-5 pb-4 [border:1px_solid_oklch(98.6%_0.002_67.8/0.14)] [background:oklch(14.7%_0.004_49.3/0.6)] rounded-[0.875rem] border-l-[3px]";

  // -- Left column: Today --
  const today = el("div", {
    class: `${COL_BASE} demo-port-col-today [border-left-color:oklch(98.6%_0.002_67.8/0.3)]`,
  });
  today.appendChild(el("div", {
    class:
      "demo-port-col-eyebrow font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.14em] text-taupe-400",
    text: "TODAY · MANUAL COORDINATION",
  }));
  for (let i = 0; i < TODAY_STEPS.length; i++) {
    const s = TODAY_STEPS[i];
    today.appendChild(el("div", {
      class:
        "demo-port-step demo-port-step-today flex flex-col gap-1.5 px-3.5 py-3 [border:1px_dashed_oklch(98.6%_0.002_67.8/0.16)] rounded-[0.625rem] [background:oklch(98.6%_0.002_67.8/0.02)]",
    }, [
      el("div", { class: "demo-port-step-row flex items-center gap-2.5 font-mono text-sm" }, [
        el("span", {
          class:
            "demo-port-step-num inline-flex items-center justify-center size-5 rounded text-[0.6875rem] font-semibold text-taupe-300 [background:oklch(98.6%_0.002_67.8/0.1)] shrink-0",
          text: String(i + 1),
        }),
        el("span", {
          class:
            "demo-port-step-name flex-1 text-[0.9375rem] font-medium [font-family:Inter,sans-serif] text-taupe-50",
          text: s.name,
        }),
        el("span", {
          class:
            "demo-port-step-hint font-mono text-xs [color:oklch(82%_0.15_35)]",
          text: s.hint,
        }),
      ]),
      el("div", {
        class:
          "demo-port-step-delegate font-mono text-[0.8125rem] text-taupe-400 ml-[1.875rem]",
        text: s.delegate,
      }),
    ]));
  }
  today.appendChild(el("div", {
    class:
      "demo-port-col-foot flex flex-col gap-1 mt-2 pt-3 [border-top:1px_solid_oklch(98.6%_0.002_67.8/0.1)]",
  }, [
    el("span", {
      class:
        "demo-port-col-stat font-display font-normal text-[clamp(1.375rem,2vw,1.75rem)] text-taupe-50 leading-tight",
      text: "21 days",
    }),
    el("span", {
      class: "demo-port-col-substat font-mono text-xs text-taupe-400",
      text: "11 hrs labor / vendor · 62% on-policy",
    }),
  ]));
  cols.appendChild(today);

  // -- Gutter: two stacked numbered eye-guides --
  const gutter = el("div", {
    class:
      "demo-port-gutter flex flex-col justify-center gap-8 text-center self-center px-2 max-lg:flex-row max-lg:gap-4",
  });
  const badge1 = stepBadge(1, "We port each step…", { align: "right", delay: 400 });
  badge1.classList.add("demo-port-gutter-badge", "flex", "flex-col", "items-center", "gap-2", "max-w-[12rem]");
  gutter.appendChild(badge1);
  const badge2 = stepBadge(2, "…Planapus picks the right hand-off", { align: "right", delay: 800 });
  badge2.classList.add("demo-port-gutter-badge", "flex", "flex-col", "items-center", "gap-2", "max-w-[12rem]");
  gutter.appendChild(badge2);
  cols.appendChild(gutter);

  // -- Right column: With Planapus (interactive) --
  const planapus = el("div", {
    class: `${COL_BASE} demo-port-col-planapus [border-left-color:oklch(82%_0.15_35)]`,
  });
  planapus.appendChild(el("div", {
    class:
      "demo-port-col-eyebrow font-mono text-[0.6875rem] font-semibold uppercase tracking-[0.14em] [color:oklch(82%_0.15_35)]",
    text: "WITH PLANAPUS · COORDINATED BY AI · CLICK TO CONFIGURE",
  }));

  // Per-step state + DOM refs so we can re-render in place on toggle.
  const stepState = PLANAPUS_STEPS.map(s => ({ pick: s.pick }));
  const stepRefs = [];

  function applyStep(i) {
    const s = PLANAPUS_STEPS[i];
    const refs = stepRefs[i];
    const pick = stepState[i].pick;
    const opt = s.options[pick];
    const meta = TYPE_META[pick];
    refs.delegateIcon.innerHTML = meta.icon;
    refs.delegateIcon.style.color = meta.color;
    refs.delegateName.textContent = opt.delegate;
    refs.chip.textContent = `${meta.label} · ${opt.sla}`;
    refs.chip.style.color = meta.color;
    refs.chip.style.borderColor = `color-mix(in srgb, ${meta.color} 40%, transparent)`;
    for (const [type, btn] of Object.entries(refs.toggleBtns)) {
      btn.classList.toggle("is-active", type === pick);
    }
  }

  function applyFooter() {
    // Cycle = sum(days) + 4 day buffer (queue/batching);
    // labor = sum(labor); on-policy = 75 base + sum(deltas), clamped 60–95.
    let days = 0, labor = 0, deltas = 0;
    for (let i = 0; i < PLANAPUS_STEPS.length; i++) {
      const opt = PLANAPUS_STEPS[i].options[stepState[i].pick];
      days += opt.days;
      labor += opt.labor;
      deltas += opt.onPolicyDelta;
    }
    const cycle = (4 + days).toFixed(1);
    const laborStr = labor.toFixed(1);
    const onPolicy = Math.max(60, Math.min(95, Math.round(75 + deltas)));
    footStat.textContent = `${cycle} days`;
    footSubstat.textContent = `${laborStr} hrs labor / vendor · ${onPolicy}% on-policy`;
  }

  for (let i = 0; i < PLANAPUS_STEPS.length; i++) {
    const s = PLANAPUS_STEPS[i];
    const stepEl = el("div", {
      class:
        "demo-port-step demo-port-step-planapus flex flex-col gap-1.5 px-3.5 py-3 [border:1px_solid_oklch(98.6%_0.002_67.8/0.12)] rounded-[0.625rem] [background:oklch(98.6%_0.002_67.8/0.02)]",
    });

    // Top row: number + name + 4 toggle icons
    const toggleBar = el("div", {
      class: "demo-port-step-toggles inline-flex items-center gap-1 shrink-0",
    });
    const toggleBtns = {};
    for (const type of TYPE_ORDER) {
      const meta = TYPE_META[type];
      // `is-active` swap is engine-toggled; the matching highlight
      // rule still lives in pilot-engine.css.
      const btn = el("button", {
        class:
          "demo-port-step-toggle inline-flex items-center justify-center size-7 p-0 [border:1px_solid_oklch(98.6%_0.002_67.8/0.18)] bg-transparent rounded-md text-taupe-400 cursor-pointer transition-colors hover:[background:oklch(98.6%_0.002_67.8/0.08)] hover:text-taupe-100 [&>svg]:size-3.5",
        type: "button",
        title: `${meta.label}: ${s.options[type].delegate}`,
        "data-type": type,
        html: meta.icon,
        onclick: () => {
          stepState[i].pick = type;
          applyStep(i);
          applyFooter();
        },
      });
      toggleBtns[type] = btn;
      toggleBar.appendChild(btn);
    }

    stepEl.appendChild(el("div", {
      class: "demo-port-step-row flex items-center gap-2.5 font-mono text-sm",
    }, [
      el("span", {
        class:
          "demo-port-step-num inline-flex items-center justify-center size-5 rounded text-[0.6875rem] font-semibold shrink-0 [background:oklch(82%_0.15_35/0.18)] [color:oklch(82%_0.15_35)]",
        text: String(i + 1),
      }),
      el("span", {
        class:
          "demo-port-step-name flex-1 text-[0.9375rem] font-medium [font-family:Inter,sans-serif] text-taupe-50",
        text: s.name,
      }),
      toggleBar,
    ]));

    // Delegate row: icon + name + chip
    const delegateIcon = el("span", {
      class:
        "demo-port-step-delegate-icon inline-flex items-center justify-center size-[1.125rem] [color:oklch(82%_0.15_35)] shrink-0 [&>svg]:size-full",
    });
    const delegateName = el("span", {
      class:
        "demo-port-step-delegate-name flex-1 font-mono text-[0.8125rem] text-taupe-300",
    });
    const chip = el("span", {
      class:
        "demo-port-step-chip inline-flex items-center px-2.5 py-0.5 [border:1px_solid_currentColor] rounded-full font-mono text-[0.6875rem] font-semibold tracking-wider whitespace-nowrap",
    });
    stepEl.appendChild(el("div", {
      class: "demo-port-step-delegate-row flex items-center gap-2 ml-[1.875rem]",
    }, [
      delegateIcon, delegateName, chip,
    ]));

    planapus.appendChild(stepEl);
    stepRefs.push({ delegateIcon, delegateName, chip, toggleBtns });
    applyStep(i);
  }

  // Footer (dynamic — updates on every toggle). The Planapus column
  // shows the stat in the brand accent color via the
  // `.demo-port-col-planapus .demo-port-col-stat` rule in CSS.
  const footStat = el("span", {
    class:
      "demo-port-col-stat font-display font-normal text-[clamp(1.375rem,2vw,1.75rem)] leading-tight [color:oklch(82%_0.15_35)]",
  });
  const footSubstat = el("span", {
    class: "demo-port-col-substat font-mono text-xs text-taupe-400",
  });
  planapus.appendChild(el("div", {
    class:
      "demo-port-col-foot flex flex-col gap-1 mt-2 pt-3 [border-top:1px_solid_oklch(98.6%_0.002_67.8/0.1)]",
  }, [footStat, footSubstat]));
  applyFooter();

  cols.appendChild(planapus);

  root.appendChild(cols);

  // Bottom caption — Story-page callback.
  root.appendChild(el("div", {
    class:
      "demo-port-caption text-center font-display italic text-[0.9375rem] text-taupe-300 my-2",
    text: "Same work. Same people. Different routing.",
  }));

  // Continue
  root.appendChild(el("button", {
    class:
      "demo-port-next absolute bottom-6 right-[clamp(1.25rem,4vw,3rem)] inline-flex items-center gap-2 px-5 py-2 border-none bg-taupe-50 text-taupe-950 rounded-full text-sm font-medium cursor-pointer transition-colors hover:bg-taupe-200 z-[6]",
    type: "button",
    onclick: () => onNext(),
  }, [
    el("span", { text: "Run it →" }),
  ]));

  return root;
}

// ---------- Scene 5 · Run + Before/After ----------
// Two-stage scene. Stage A runs a single hero vendor through the
// onboarding workflow live (compressed clock, signals strip, an
// active-step arrow). Stage B fades in once Stage A finishes and
// shows the Today-vs-Planapus comparison with horizontal bars.

function renderRun(onNext) {
  const root = el("div", { class: "demo-run" });

  // Header
  root.appendChild(el("div", { class: "demo-run-head" }, [
    el("div", { class: "demo-run-eyebrow", text: "STEP 4 · RUN" }),
    el("h2", { class: "demo-run-title", text: "Watch Planapus run it." }),
  ]));

  // --- Stage A : the running hero card ---
  const stageA = el("div", { class: "demo-run-stage-a" });

  stageA.appendChild(el("div", { class: "demo-run-clock", text: "T+0 · 1s = 1h" }));

  const STEPS_LBL = ["BRIEF", "SEC", "FIN", "INS", "ACTIVATE", "LIVE"];

  const heroCard = el("div", { class: "demo-run-hero" });
  const heroStatus = el("span", { class: "demo-run-hero-status", text: "RUNNING" });
  const heroHead = el("div", { class: "demo-run-hero-head" }, [
    el("span", { class: "demo-run-hero-init", text: "H" }),
    el("div", { class: "demo-run-hero-names" }, [
      el("div", { class: "demo-run-hero-name", text: "Halcyon Print & Mailing" }),
      el("div", { class: "demo-run-hero-meta", text: "$148K annual spend · 4 blockers · activating now" }),
    ]),
    heroStatus,
  ]);
  heroCard.appendChild(heroHead);

  const trackWrap = el("div", { class: "demo-run-trackwrap" });
  const track = el("div", { class: "demo-run-track" });
  track.appendChild(el("div", { class: "demo-run-trackbase" }));
  const trackFill = el("div", { class: "demo-run-trackfill" });
  track.appendChild(trackFill);
  const stepEls = STEPS_LBL.map((lbl, s) => {
    const sw = el("div", {
      class: "demo-run-step",
      style: `left: calc(10px + (100% - 20px) * ${s / (STEPS_LBL.length - 1)});`,
    }, [
      el("div", { class: "demo-run-stepdot" }),
      el("div", { class: "demo-run-steplbl", text: lbl }),
    ]);
    track.appendChild(sw);
    return sw;
  });
  trackWrap.appendChild(track);
  heroCard.appendChild(trackWrap);

  const heroFootText = el("span", { class: "demo-run-hero-foottext", text: "" });
  heroCard.appendChild(el("div", { class: "demo-run-hero-foot" }, [heroFootText]));
  stageA.appendChild(heroCard);

  // Floating arrow + badge — "Planapus runs it. You watch."
  const arrow1 = stepBadge(1, "Planapus runs it. You watch.", { align: "right", delay: 400 });
  arrow1.classList.add("demo-run-arrow1");
  stageA.appendChild(arrow1);

  // Live signals strip (3 entries — not the 6 the legacy Operate scene had).
  const SIGNALS = [
    { time: "T+1h",  label: "NetSuite PO line opened" },
    { time: "T+12h", label: "Vanta DPA returned clean" },
    { time: "T+24h", label: "Insurance cert validated" },
  ];
  const signals = el("div", { class: "demo-run-signals" });
  signals.appendChild(el("div", { class: "demo-run-signals-head", text: "Live signals" }));
  for (let i = 0; i < SIGNALS.length; i++) {
    const s = SIGNALS[i];
    signals.appendChild(el("div", {
      class: "demo-run-signal",
      style: `--delay: ${800 + i * 900}ms;`,
    }, [
      el("span", { class: "demo-run-signal-dot" }),
      el("span", { class: "demo-run-signal-time", text: s.time }),
      el("span", { class: "demo-run-signal-label", text: s.label }),
    ]));
  }
  stageA.appendChild(signals);

  root.appendChild(stageA);

  // --- Stage B : Before / After (hidden until Stage A completes) ---
  const stageB = el("div", { class: "demo-run-stage-b" });
  stageB.appendChild(el("div", { class: "demo-run-stage-b-head" }, [
    el("div", { class: "demo-run-stage-b-eyebrow", text: "BEFORE  ·  AFTER" }),
  ]));
  const cols = el("div", { class: "demo-run-cols" }, [
    el("div", { class: "demo-run-col demo-run-col-today" }, [
      el("div", { class: "demo-run-col-eyebrow", text: "TODAY · MANUAL COORDINATION" }),
      el("div", { class: "demo-run-col-stat", text: "21 days" }),
      el("div", { class: "demo-run-col-substat", text: "11 hrs labor · 62% on-policy · 1 vendor at a time" }),
      el("div", { class: "demo-run-bar demo-run-bar-today" }),
    ]),
    el("div", { class: "demo-run-col demo-run-col-planapus" }, [
      el("div", { class: "demo-run-col-eyebrow", text: "WITH PLANAPUS · COORDINATED BY AI" }),
      el("div", { class: "demo-run-col-stat", text: "7 days" }),
      el("div", { class: "demo-run-col-substat", text: "2.4 hrs labor · 95% on-policy · 9 vendors in parallel" }),
      el("div", { class: "demo-run-bar demo-run-bar-planapus" }),
    ]),
  ]);
  stageB.appendChild(cols);

  const summary = el("div", { class: "demo-run-summary" });
  summary.appendChild(el("div", { class: "demo-run-summary-text", text:
    "+$3.6M reclaimed across 60 vendors in 21 days. Same work. Same people. Different routing." }));
  const badge2 = stepBadge(2, "The win", { align: "right", delay: 700 });
  badge2.classList.add("demo-run-summary-badge");
  summary.appendChild(badge2);
  stageB.appendChild(summary);

  stageB.appendChild(el("button", {
    class: "demo-run-next",
    type: "button",
    onclick: () => onNext(),
  }, [
    el("span", { text: "See the scale →" }),
  ]));

  root.appendChild(stageB);

  // --- Animation: hero card runs through STEPS_LBL ---
  let progress = 0;
  let tickTimer = null;
  let stageBTimer = null;
  const TICK_MS = 70;
  const ADVANCE_PER_TICK = 1.6;
  function applyProgress() {
    const clamped = Math.min(100, Math.max(0, progress));
    trackFill.style.width = `calc((100% - 20px) * ${clamped / 100})`;
    for (let s = 0; s < stepEls.length; s++) {
      const pct = (s / (stepEls.length - 1)) * 100;
      const reached = clamped >= pct;
      stepEls[s].classList.toggle("is-passed", reached);
    }
    if (clamped < 100) {
      const stepIdx = Math.min(STEPS_LBL.length - 1, Math.floor((clamped / 100) * STEPS_LBL.length));
      heroFootText.textContent = `PROCESSING ${STEPS_LBL[stepIdx]}…`;
    } else {
      heroFootText.textContent = "ACTIVATED · $148K · 7 day cycle";
      heroStatus.textContent = "COMPLETE";
      heroCard.classList.add("is-complete");
    }
  }
  function reset() {
    if (tickTimer) clearInterval(tickTimer);
    if (stageBTimer) clearTimeout(stageBTimer);
    progress = 0;
    heroCard.classList.remove("is-complete");
    heroStatus.textContent = "RUNNING";
    heroFootText.textContent = "";
    root.classList.remove("is-stage-b-ready");
    applyProgress();
    tickTimer = setInterval(() => {
      progress += ADVANCE_PER_TICK;
      applyProgress();
      if (progress >= 100) {
        clearInterval(tickTimer);
        tickTimer = null;
        stageBTimer = setTimeout(() => {
          root.classList.add("is-stage-b-ready");
        }, 700);
      }
    }, TICK_MS);
  }
  root.__reset = reset;
  root.__cleanup = () => {
    if (tickTimer) clearInterval(tickTimer);
    if (stageBTimer) clearTimeout(stageBTimer);
  };
  setTimeout(reset, 200);

  return root;
}

// ---------- Scene 6 · Scale ----------
// Multi-process ROI math + 4-week onramp + final CTA. Replaces the
// closing-scene "Vision" graph; the graph layer still persists behind
// (via the background SVG layer) but the foreground is now the
// quantified scale story.

function renderScale(onPrev) {
  const root = el("div", { class: "demo-scale" });

  root.appendChild(el("div", { class: "demo-scale-eyebrow", text:
    "STEP 5 · WIN AT SCALE" }));
  root.appendChild(el("h2", { class: "demo-scale-title", text:
    "One process today. Twenty-three across the org." }));

  // ROI rollup — same numbers as the previous Closing-scene rollup,
  // but now the primary content of the scene.
  const roi = el("div", { class: "demo-scale-roi" }, [
    el("div", { class: "demo-scale-roi-row", style: `--delay: 200ms;` }, [
      el("span", { class: "demo-scale-roi-scope", text: "1 process" }),
      el("span", { class: "demo-scale-roi-rate",  text: "60 vendors / qtr" }),
      el("span", { class: "demo-scale-roi-fig",   text: "$3.6M reclaimed" }),
    ]),
    el("div", { class: "demo-scale-roi-row", style: `--delay: 380ms;` }, [
      el("span", { class: "demo-scale-roi-scope", text: "5 processes" }),
      el("span", { class: "demo-scale-roi-rate",  text: "~1,200 runs / yr" }),
      el("span", { class: "demo-scale-roi-fig",   text: "$14M reclaimed" }),
    ]),
    el("div", { class: "demo-scale-roi-row", style: `--delay: 560ms;` }, [
      el("span", { class: "demo-scale-roi-scope", text: "23 processes" }),
      el("span", { class: "demo-scale-roi-rate",  text: "full deployment" }),
      el("span", { class: "demo-scale-roi-fig",   text: "$58M reclaimed · 31,000 hrs returned" }),
    ]),
  ]);
  root.appendChild(roi);

  // 4-week on-ramp.
  const onramp = el("div", { class: "demo-scale-onramp" });
  onramp.appendChild(el("div", { class: "demo-scale-onramp-head", text: "On-ramp" }));
  const steps = el("div", { class: "demo-scale-onramp-steps" });
  const ONRAMP = [
    { week: "Week 1",    text: "Connect sources, ingest contracts & operations docs (read-only)" },
    { week: "Weeks 2–3", text: "Validate top 3 derived operations with your procurement lead" },
    { week: "Week 4",    text: "Go live with human-in-the-loop · expand monthly" },
  ];
  for (let i = 0; i < ONRAMP.length; i++) {
    const s = ONRAMP[i];
    steps.appendChild(el("div", {
      class: "demo-scale-onramp-step",
      style: `--delay: ${800 + i * 140}ms;`,
    }, [
      el("span", { class: "demo-scale-onramp-step-week", text: s.week }),
      el("span", { class: "demo-scale-onramp-step-text", text: s.text }),
    ]));
  }
  onramp.appendChild(steps);
  onramp.appendChild(el("div", {
    class: "demo-scale-onramp-caption",
    text: "White-glove onboarding included for districts and large enterprises.",
  }));
  root.appendChild(onramp);

  // CTA wires to the existing topbar "Talk to us" lead flow by simply
  // clicking that button — keeps a single source of truth for the lead
  // capture modal.
  const cta = el("button", {
    class: "demo-scale-cta",
    type: "button",
    onclick: () => {
      const talk = document.getElementById("topbar-talk");
      if (talk) talk.click();
    },
  }, [
    el("span", { text: "Talk to us" }),
    el("span", { class: "demo-scale-cta-arrow", text: "→" }),
  ]);
  // Numbered callout next to the CTA — uses the shared eye-guidance
  // helper so it stays visually consistent with future scene arrows.
  const ctaWrap = el("div", { class: "demo-scale-cta-wrap" }, [
    cta,
    stepBadge(3, "Apply to your org", { align: "right", delay: 1100 }),
  ]);
  root.appendChild(ctaWrap);

  // Back link to Scene 5.
  root.appendChild(el("button", {
    class: "demo-scale-back",
    type: "button",
    text: "← Back",
    onclick: () => onPrev(),
  }));

  return root;
}

// ---------- BackgroundGraph highlight pipeline ----------
// Used by Scene 3 Map to light up the hero cross-edge + its adjacent
// nodes. The pipeline mutates SVG element classes directly — no state,
// no rendering — keyed off the precomputed cross-edge + adjacency
// indexes built at init.

function computeHighlightSet(processIdx) {
  if (processIdx == null || !highlightIndex) return null;
  const { cross, adj } = highlightIndex;
  const focus = cross[processIdx % cross.length];
  if (!focus) return null;
  const hEdges = new Set([focus.id]);
  const hNodes = new Set([focus.source, focus.target]);
  // Add up to 3 adjacent edges per endpoint.
  for (const endpoint of [focus.source, focus.target]) {
    const adjEdges = (adj.get(endpoint) || []).slice(0, 3);
    for (const e of adjEdges) {
      hEdges.add(e.id);
      hNodes.add(e.source);
      hNodes.add(e.target);
    }
  }
  // For each highlighted process node, also pull in its parent system edge.
  for (const n of cachedGraph.nodes) {
    if (!hNodes.has(n.id) || n.type !== "process") continue;
    for (const e of cachedGraph.edges) {
      const linksProcess = (e.source === n.id && String(e.target).startsWith("sys-")) ||
                           (e.target === n.id && String(e.source).startsWith("sys-"));
      if (linksProcess) {
        hEdges.add(e.id);
        hNodes.add(e.source);
        hNodes.add(e.target);
      }
    }
  }
  return { edges: hEdges, nodes: hNodes };
}

// Apply (or clear) the Closing-scene state on the BackgroundGraph: primary
// highlight on cross-edge 0 plus a softer "secondary" treatment on the four
// picked cross-edges (the named latent processes) plus the SVG labels.

function applyBgHighlight(processIdx) {
  if (!bg) return;
  const set = computeHighlightSet(processIdx);
  // Edges
  const edgeNodes = bg.edgesG.children;
  for (let i = 0; i < edgeNodes.length; i++) {
    const ln = edgeNodes[i];
    const id = ln.getAttribute("data-eid");
    ln.classList.remove("is-highlighted", "is-dimmed");
    if (set) {
      if (set.edges.has(id)) ln.classList.add("is-highlighted");
      else ln.classList.add("is-dimmed");
    }
  }
  // Nodes
  const nodeNodes = bg.nodesG.children;
  for (let i = 0; i < nodeNodes.length; i++) {
    const cn = nodeNodes[i];
    const id = cn.getAttribute("data-nid");
    cn.classList.remove("is-highlighted", "is-dimmed");
    const n = cachedGraph.nodes.find(x => x.id === id);
    if (set) {
      if (set.nodes.has(id)) {
        cn.classList.add("is-highlighted");
        cn.setAttribute("r", n && n.type === "system" ? 8 : 4);
      } else {
        cn.classList.add("is-dimmed");
        cn.setAttribute("r", n && n.type === "system" ? 6 : 3);
      }
    } else {
      cn.setAttribute("r", n && n.type === "system" ? 6 : 3);
    }
  }
}

// ---------- Act 1: Discover ----------
// Five-phase reveal. The modal masks the graph until the simulated scan
// completes; then it fades out and exposes the side panel with latent
// processes.

// ---------- Act 2: Detect ----------
// Four-phase progressive disclosure with auto-progression timers.


// 9 parallel orchestrations, simulated progress ticker, live signals + risks.



// ---------- View state ----------

let initialized = false;
let active = false;
let cachedGraph = null;
let highlightIndex = null;
let bg = null;
let scroller = null;
let primaryClosingIdx = 0;   // Index into highlightIndex.cross — most-central cross-edge after layout; used by Scene 3 Map's hero highlight.
let scenes = [];
let dotEls = [];
let activeScene = 1;
let observer = null;
let keyHandler = null;
let sceneContents = [];      // [{ root }]

export function setActiveScene(idx) {
  if (idx === activeScene) return;
  // Notify outgoing scene
  const out = sceneContents[activeScene - 1];
  if (out && out.root.__cleanup) out.root.__cleanup();
  // Update state
  activeScene = idx;
  for (let i = 0; i < dotEls.length; i++) {
    dotEls[i].classList.toggle("demo-dot-active", i === idx - 1);
  }
  const section = document.getElementById("demo");
  if (section) section.setAttribute("data-act", String(idx));
  // Notify incoming scene
  const inc = sceneContents[idx - 1];
  if (inc && inc.root.__reset) inc.root.__reset();
}

function goToScene(idx) {
  const clamped = Math.max(1, Math.min(SCENES.length, idx));
  const sceneEl = scenes[clamped - 1];
  if (sceneEl && scroller) {
    scroller.scrollTo({ top: sceneEl.offsetTop, behavior: "smooth" });
  }
}

function buildDots() {
  // F17 Slice B: Tailwind utilities replace the prior `.demo-dots` +
  // `.demo-dot*` rules in pilot-engine.css. State classes
  // (`.is-active`) stay engine-toggled; the matching highlight rule
  // is kept as a single CSS line.
  const dots = el("div", {
    class:
      "demo-dots absolute right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-3.5",
  });
  dotEls = [];
  for (const s of SCENES) {
    // Dot's `--label-text` content is shown via the inline
    // `.demo-dot-tip` span on hover/focus.
    const dot = el("button", {
      class:
        "demo-dot group relative size-2.5 [border:1.5px_solid_oklch(98.6%_0.002_67.8/0.4)] rounded-full bg-transparent cursor-pointer p-0 transition-transform hover:scale-[1.4]",
      type: "button",
      "aria-label": `Go to scene ${s.id}: ${s.label}`,
      onclick: () => goToScene(s.id),
    }, [
      el("span", {
        class: "demo-dot-hit absolute -inset-2.5",
      }),
      el("span", {
        class:
          "demo-dot-tip absolute right-[22px] top-1/2 -translate-y-1/2 whitespace-nowrap font-mono text-[11px] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full [background:oklch(21.4%_0.009_43.1)] [border:1px_solid_oklch(98.6%_0.002_67.8/0.18)] text-taupe-50 opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100",
        text: `${String(s.id).padStart(2, "0")} · ${s.label}`,
      }),
    ]);
    dotEls.push(dot);
    dots.appendChild(dot);
  }
  return dots;
}

export function buildShell() {
  const section = document.getElementById("demo");
  if (!section) return false;
  section.innerHTML = "";
  section.removeAttribute("data-demo-src");

  // F17 Slice B: `.demo-shell` styling inlined here. The class is
  // also redundantly applied to the React `<section id="demo">` in
  // Demo.tsx so the dark canvas + Inter font apply even before
  // bootDemo() has run.
  const shell = el("div", {
    class:
      "demo-shell relative min-h-screen [font-family:Inter,system-ui,sans-serif] text-taupe-50",
  });

  // Background graph layer.
  cachedGraph = layoutGraph(generateGraph());
  highlightIndex = buildHighlightIndex(cachedGraph);
  // Pre-compute the hero (most-central cross-edge) for Scene 3 Map.
  primaryClosingIdx = pickPrimaryClosingIdx(cachedGraph);
  bg = renderBackgroundGraph(cachedGraph);
  const bgLayer = el("div", {
    // The scene-aware opacity (`#demo[data-act="N"] .demo-bg`) lives
    // in pilot-engine.css since `data-act` is a runtime attribute on
    // a different element + has a transition.
    class: "demo-bg absolute inset-0 pointer-events-none z-0",
  });
  bgLayer.appendChild(bg.root);
  shell.appendChild(bgLayer);

  // Scroll-snap container with one section per scene.
  // Tailwind v4 has `[&::-webkit-scrollbar]:hidden` for the WebKit
  // scrollbar hide, paired with `[scrollbar-width:none]` for Firefox.
  scroller = el("div", {
    class:
      "demo-scroller relative z-10 h-[calc(100vh-var(--scroll-padding-top,5.25rem))] w-full overflow-y-auto overflow-x-hidden snap-y snap-mandatory scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
  });
  scenes = [];
  sceneContents = [];
  for (const s of SCENES) {
    const sceneEl = el("section", {
      class:
        "demo-scene relative min-h-[calc(100vh-var(--scroll-padding-top,5.25rem))] w-full snap-start overflow-hidden flex items-center justify-center px-12 py-8",
      "data-scene": String(s.id),
    });
    let content;
    if (s.id === 1) {
      content = renderHook(() => goToScene(2));
    } else if (s.id === 2) {
      content = renderRead(() => goToScene(3));
    } else if (s.id === 3) {
      content = renderMap(() => goToScene(4));
    } else if (s.id === 4) {
      content = renderPort(() => goToScene(5));
    } else if (s.id === 5) {
      content = renderRun(() => goToScene(6));
    } else {
      content = renderScale(() => goToScene(SCENES.length - 1));
    }
    sceneEl.appendChild(content);
    sceneContents.push({ root: content });
    scenes.push(sceneEl);
    scroller.appendChild(sceneEl);
  }
  shell.appendChild(scroller);
  shell.appendChild(buildDots());
  section.appendChild(shell);
  return true;
}

export function wireSceneTracking() {
  if (observer) { observer.disconnect(); observer = null; }
  observer = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (e.isIntersecting && e.intersectionRatio >= 0.5) {
        const idx = Number(e.target.getAttribute("data-scene"));
        if (idx) setActiveScene(idx);
      }
    }
  }, { root: scroller, threshold: [0.5] });
  for (const s of scenes) observer.observe(s);
}

export function wireKeyboard() {
  if (keyHandler) window.removeEventListener("keydown", keyHandler);
  keyHandler = (e) => {
    if (!active) return;
    if (e.target instanceof HTMLInputElement) return;
    if (e.target instanceof HTMLTextAreaElement) return;
    switch (e.key) {
      case "ArrowDown":
      case "PageDown":
        e.preventDefault(); goToScene(activeScene + 1); break;
      case "ArrowUp":
      case "PageUp":
        e.preventDefault(); goToScene(activeScene - 1); break;
      case "Home":
        e.preventDefault(); goToScene(1); break;
      case "End":
        e.preventDefault(); goToScene(SCENES.length); break;
    }
  };
  window.addEventListener("keydown", keyHandler);
}

// ---------- Lifecycle ----------
//
// Phase 7 of the React-engine port lifts the previous
// `activateDemo` / `deactivateDemo` trampolines into `Demo.tsx`. The
// React component owns the `<section id="demo">` via a ref and calls
// `bootDemo()` in `useEffect`, `teardownDemo()` from cleanup. The
// individual helpers (`buildShell`, `wireSceneTracking`,
// `wireKeyboard`, `setActiveScene`) remain exported for tests and
// for any future scene-level entry points; `bootDemo` is the single
// entry point that React actually invokes.
//
// `active` is still tracked here because `wireKeyboard`'s handler
// gates on it — the listener lives on `window`, not on the ref-owned
// section, so it could otherwise fire after the React component has
// unmounted if a stray reference held the handler alive.
//
// Body-class state (`demo-mode`) is owned by `PilotProvider`. Don't
// touch it here.

export function bootDemo() {
  if (!buildShell()) return false;
  wireSceneTracking();
  wireKeyboard();
  active = true;
  // Scroll to top so the IntersectionObserver and our activeScene
  // state agree on scene 1 as the entry point.
  if (scroller) scroller.scrollTop = 0;
  // Force scene 1 to fresh state via the `activeScene = 0` sentinel
  // (otherwise setActiveScene(1) early-returns because activeScene
  // already == 1 from module init). This fires scene 1's __reset
  // hook so timer-driven content starts fresh on every (re-)mount.
  activeScene = 0;
  setActiveScene(1);
  initialized = true;
  return true;
}

export function teardownDemo() {
  active = false;
  // Stop any timers left running in the currently active scene.
  const cur = sceneContents[activeScene - 1];
  if (cur && cur.root.__cleanup) cur.root.__cleanup();
  // Drop the IntersectionObserver and the keyboard handler — Demo.tsx
  // will rebuild them on the next mount.
  if (observer) { observer.disconnect(); observer = null; }
  if (keyHandler) {
    window.removeEventListener("keydown", keyHandler);
    keyHandler = null;
  }
  // Reset module-level state so the next mount rebuilds from scratch.
  // The actual DOM is owned by the React-managed <section>, which
  // unmounted alongside this teardown, so the cached references would
  // be dangling otherwise.
  initialized = false;
  cachedGraph = null;
  highlightIndex = null;
  bg = null;
  scroller = null;
  scenes = [];
  dotEls = [];
  sceneContents = [];
  activeScene = 1;
}
